'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Check, ChevronDown, CheckCircle2, Loader2 } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { HeroGlobeBand } from '@/components/layout/HeroGlobeBand';
import { getApiErrorMessage, pricingApi, subscriptionApi, type PublicSubscriptionPlan } from '@/lib/api';
import { labelForPermissionKey } from '@/lib/organizer-permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Modal';
import { useAuthStore } from '@/lib/auth-store';
import { canBecomeOrganizer, isOrganizerRole, isOrganizationStaffRole } from '@/lib/rbac';
import { triggerCustomPlanRequest } from '@/components/plans/UpgradeModal';
import { PlanCard } from '@/components/plans/PlanCard';
import { PLANS_CONFIG, formatParticipantLimit } from '@/config/plans.config';
import toast from 'react-hot-toast';

type SubscriptionPlan = {
  id: string;
  key?: string;
  name: string;
  description?: string | null;
  commission_percent?: number;
  gst_percent?: number;
  platform_fee_percent?: number;
  registration_limit?: number | null;
  fees?: {
    platform_fee_percent: number;
    gateway_fee_percent: number;
    gst_percent: number;
    gst_applicable: boolean;
  };
  limits?: {
    participants_max?: number | null;
    campaigns_max?: number | null;
    team_members_max?: number | null;
    tickets_max?: number | null;
    events_max?: number | null;
  };
  features?: { id: string; name: string; slug: string; key?: string; category?: string | null; enabled?: boolean }[];
  bundles?: { id: string; key?: string; name: string }[];
  event_types?: { id: string; key: string; name: string }[];
  payment_methods?: string[];
  is_active?: boolean;
  is_default: boolean;
  is_popular?: boolean;
  is_recommended?: boolean;
  is_enterprise: boolean;
  display_order: number;
};

type PublicSubscriptionPlanWithRecommendation = PublicSubscriptionPlan & {
  is_recommended?: unknown;
  bundles?: { id: string; key?: string; name: string }[];
  event_types?: { id: string; key: string; name: string }[];
};

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function cleanPlanDescription(plan: { key?: string; is_enterprise?: boolean; description?: string | null }): string {
  const desc = plan.description?.trim();
  const key = (plan.key || '').toLowerCase();

  // If description is empty or has contradictory hardcoded fee/commission text from legacy seeders, use modern copy
  if (!desc || /(\d+(\.\d+)?%|\bcommission\b)/i.test(desc)) {
    if (key === 'free') return 'Get started with basic community event management for free events.';
    if (key === 'bronze') return 'Ticketing, attendee communication, and registrations for growing events.';
    if (key === 'silver') return 'Complete feature access for competitive and large events up to 3,000 participants.';
    if (key === 'custom' || plan.is_enterprise) return 'Bespoke high-volume solutions with dedicated support, custom platform fee rates, and custom SLAs.';
  }

  return desc ?? '';
}

function normalizePlan(raw: PublicSubscriptionPlanWithRecommendation): SubscriptionPlan {
  const rawKey = (raw.key || raw.slug || '').toLowerCase();
  const rawChecklist = (raw as any).checklist;
  const rawFeatures = Array.isArray(raw.features) && raw.features.length > 0 && typeof raw.features[0] === 'string'
    ? raw.features
    : rawChecklist;
  const fallbackChecklist = PLANS_CONFIG[rawKey]?.checklist;
  const sourceFeatures = Array.isArray(rawFeatures) && rawFeatures.length > 0 ? rawFeatures : (fallbackChecklist || []);

  const normalizedFeatures = sourceFeatures
    .map((feature: any, index: number) => {
      if (typeof feature === 'string') {
        const label = (feature.startsWith('module.') || (feature.includes('.') && !feature.includes(' ')))
          ? (labelForPermissionKey(feature) || feature)
          : feature;
        return {
          id: `${raw.id}-feature-${index}`,
          name: label,
          slug: feature.toLowerCase().replace(/\s+/g, '-'),
          key: feature.toLowerCase().replace(/\s+/g, '_'),
          category: 'Plan modules',
          enabled: true,
        };
      }
      const rawKey = feature.key || feature.slug || '';
      const label = (rawKey.startsWith('module.') || (rawKey.includes('.') && !rawKey.includes(' ')))
        ? (labelForPermissionKey(rawKey) || feature.name || rawKey)
        : (feature.name || rawKey || 'Feature');

      return {
        id: feature.id ?? `${raw.id}-feature-${index}`,
        name: label,
        slug: feature.slug ?? feature.key ?? `feature-${index}`,
        key: feature.key ?? feature.slug ?? `feature_${index}`,
        category: feature.category || 'Plan modules',
        enabled: true,
      };
    })
    .filter((feature: any) => Boolean(feature.name));

  const platformFee = raw.platform_fee_percent != null
    ? Number(raw.platform_fee_percent)
    : raw.fees?.platform_fee_percent != null
      ? Number(raw.fees.platform_fee_percent)
      : Number(raw.commission_percent ?? 0);

  const gstPercent = raw.gst_percent != null
    ? Number(raw.gst_percent)
    : raw.fees?.gst_percent != null
      ? Number(raw.fees.gst_percent)
      : 18;

  const cleanedDesc = cleanPlanDescription({ key: rawKey, is_enterprise: Boolean(raw.is_enterprise), description: raw.description });

  return {
    id: String(raw.id),
    key: rawKey,
    name: raw.name,
    description: cleanedDesc,
    commission_percent: platformFee,
    gst_percent: gstPercent,
    platform_fee_percent: platformFee,
    registration_limit: raw.limits?.participants_max ?? null,
    fees: {
      platform_fee_percent: platformFee,
      gateway_fee_percent: Number(raw.fees?.gateway_fee_percent ?? 0),
      gst_percent: gstPercent,
      gst_applicable: Boolean(raw.fees?.gst_applicable ?? true),
    },
    limits: raw.limits ?? {},
    features: normalizedFeatures,
    bundles: raw.bundles ?? [],
    event_types: raw.event_types ?? [],
    payment_methods: raw.payment_methods ?? [],
    is_active: raw.is_active ?? true,
    is_default: toBool(raw.is_default),
    is_popular: toBool(raw.is_popular),
    is_recommended: toBool(raw.is_recommended),
    is_enterprise: Boolean(raw.is_enterprise),
    display_order: Number(raw.display_order ?? raw.sort_order ?? 0),
  };
}

const PLAN_ORDER = ['free', 'bronze', 'silver', 'custom'];

function planRank(plan: SubscriptionPlan): number {
  const haystack = `${plan.key ?? ''} ${plan.name}`.toLowerCase();
  const index = PLAN_ORDER.findIndex((tier) => haystack.includes(tier));
  return index >= 0 ? index : 99 + (plan.display_order ?? 0);
}

function planTone(plan: SubscriptionPlan): { eyebrow: string; accent: string; selected: string } {
  const haystack = `${plan.key ?? ''} ${plan.name}`.toLowerCase();
  if (haystack.includes('custom') || plan.is_enterprise) {
    return {
      eyebrow: 'Custom tier',
      accent: '04',
      selected: 'Tailored for scale',
    };
  }
  if (haystack.includes('silver')) {
    return {
      eyebrow: 'Silver tier',
      accent: '03',
      selected: 'Most popular',
    };
  }
  if (haystack.includes('bronze')) {
    return {
      eyebrow: 'Bronze tier',
      accent: '02',
      selected: 'Growth starter',
    };
  }
  return {
    eyebrow: 'Free tier',
    accent: '01',
    selected: 'Start free',
  };
}

function formatLimit(value?: number | null): string {
  if (value == null) return 'Zero';
  if (value <= 0) return 'Unlimited';
  return value.toLocaleString();
}

function groupedModules(plan: SubscriptionPlan): Array<{ name: string; modules: string[] }> {
  const groups = new Map<string, string[]>();
  for (const feature of plan.features ?? []) {
    const groupName = feature.category || 'Plan modules';
    const current = groups.get(groupName) ?? [];
    if (!current.includes(feature.name)) {
      groups.set(groupName, [...current, feature.name]);
    }
  }

  if (groups.size === 0 && plan.key && PLANS_CONFIG[plan.key]?.checklist) {
    return [{ name: 'Plan modules', modules: PLANS_CONFIG[plan.key].checklist }];
  }

  return Array.from(groups.entries()).map(([name, modules]) => ({ name, modules }));
}

export default function PricingPage() {
  const router = useRouter();
  const { user, isAuthenticated } = useAuthStore();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedModulePlanIds, setExpandedModulePlanIds] = useState<Set<string>>(() => new Set());
  const [currentPlanKey, setCurrentPlanKey] = useState<string | null>(null);
  const [switchingPlan, setSwitchingPlan] = useState<SubscriptionPlan | null>(null);
  const [switchingLoading, setSwitchingLoading] = useState(false);

  const isUserOrganizer = Boolean(
    isAuthenticated && user && (
      isOrganizerRole(user.role) ||
      isOrganizerRole(user.user_type) ||
      isOrganizationStaffRole(user.role) ||
      isOrganizationStaffRole(user.user_type) ||
      !canBecomeOrganizer(user)
    )
  );

  useEffect(() => {
    if (!isAuthenticated || !isUserOrganizer) return;
    subscriptionApi.current()
      .then((res) => {
        const curData = res.data?.data;
        const key = (curData?.current_plan_key || curData?.plan?.key || curData?.subscription?.plan || '').toLowerCase();
        if (key) setCurrentPlanKey(key);
      })
      .catch(() => {});
  }, [isAuthenticated, isUserOrganizer]);

  const togglePlanModules = useCallback((planId: string) => {
    setExpandedModulePlanIds((current) => {
      const next = new Set(current);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  }, []);

  const loadPlans = useCallback(() => {
    setLoading(true);
    setError(null);
    pricingApi
      .publicPlans({ fresh: true })
      .catch(() => pricingApi.subscriptionPlans({ fresh: true }))
      .then((res) => {
        const data = res.data?.data ?? [];
        const normalized = (Array.isArray(data) ? data : [])
          .map((plan) => normalizePlan(plan))
          .filter((plan) => plan.is_active !== false)
          .filter((plan) => plan.key !== 'gold' && plan.name.toLowerCase() !== 'gold')
          .filter((plan) => Number(plan.fees?.platform_fee_percent ?? 0) >= 0)
          .sort((a, b) => planRank(a) - planRank(b));
        setPlans(normalized);
      })
      .catch((err: unknown) => {
        const status = (err as any)?.response?.status;
        if (!(err as any)?.response || status >= 500) {
          setPlans([]);
          setError(getApiErrorMessage(err, 'Failed to load plans.'));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    let lastFetched = Date.now();
    const refreshIfStale = () => {
      // Avoid firing rapid concurrent requests on every window/tab switch
      if (Date.now() - lastFetched > 60000) {
        lastFetched = Date.now();
        loadPlans();
      }
    };
    window.addEventListener('focus', refreshIfStale);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshIfStale();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadPlans]);

  const normalizedPlans = useMemo(() => {
    return [...plans].sort((a, b) => {
      const rank = planRank(a) - planRank(b);
      if (rank !== 0) return rank;
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });
  }, [plans]);

  return (
    <MainLayout
      showCreateEvent={false}
      showGlobeBackground={false}
      contentClassName="mx-auto max-w-7xl space-y-10"
      heroHeader={
        <HeroGlobeBand
          badge="Live Subscription Plans"
          title="Simple Pricing for Modern Events"
          subtitle="Compare feature coverage, limits, and platform fees instantly. Plans are fully managed from your admin control tower."
        />
      }
    >
      <section className="grid gap-5 lg:grid-cols-4">
        {isUserOrganizer && (
          <div className="lg:col-span-4 rounded-2xl border border-emerald-200/90 bg-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
            <div className="flex items-center gap-3.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
                    Organizer Account Active
                  </p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100/80 text-emerald-800 border border-emerald-200 capitalize">
                    {currentPlanKey || 'Active'} Tier
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-0.5">
                  Your organization is currently on the <strong className="font-semibold text-slate-900 capitalize">{currentPlanKey || 'Active'}</strong> tier. Select any plan below to upgrade, switch, or manage features.
                </p>
              </div>
            </div>
            <Link
              href="/dashboard/settings/subscription"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-800 hover:bg-stone-50 hover:border-stone-300 transition shadow-2xs shrink-0 cursor-pointer"
            >
              <span>Subscription settings</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        )}

        {loading && (
          <div className="lg:col-span-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((slot) => (
              <div key={`pricing-skeleton-${slot}`} className="rounded-2xl border border-landing-border bg-bg p-5 animate-pulse space-y-3 shadow-sm">
                <div className="h-5 w-1/2 rounded bg-landing-border/70" />
                <div className="h-4 w-2/3 rounded bg-landing-border/70" />
                <div className="h-20 rounded bg-landing-border/60" />
                <div className="h-10 rounded bg-landing-border/70" />
              </div>
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="lg:col-span-4 rounded-2xl border border-mist/60 bg-mist/30 p-6 text-center">
            <p className="text-sm text-soot">{error}</p>
            <Button className="mt-3" variant="outline" onClick={loadPlans}>
              Retry
            </Button>
          </div>
        )}
        {!loading && normalizedPlans.length === 0 && (
          <div className="lg:col-span-4 rounded-2xl border border-landing-border bg-bg p-6 text-center text-sm text-landing-muted">
            No active plans are currently available.
          </div>
        )}

        {normalizedPlans.map((plan) => {
          const planKey = (plan.key || plan.name || '').toLowerCase();
          const isCurrent = Boolean(
            isUserOrganizer &&
            currentPlanKey &&
            (planKey === currentPlanKey.toLowerCase() || plan.name.toLowerCase() === currentPlanKey.toLowerCase())
          );
          const isUpgrade = Boolean(
            currentPlanKey && planRank(plan) > PLAN_ORDER.findIndex((t) => currentPlanKey.includes(t))
          );

          return (
            <PlanCard
              key={plan.id}
              plan={plan as any}
              variant="pricing"
              isCurrent={isCurrent}
              showCampaignsCard={true}
              actionButton={
                plan.key === 'custom' || plan.is_enterprise ? (
                  <button
                    type="button"
                    onClick={() => triggerCustomPlanRequest()}
                    className="flex h-11 w-full items-center justify-center rounded-xl border border-stone-300 bg-white text-sm font-semibold text-slate-800 transition-all hover:bg-[#1C3B2B] hover:text-white hover:border-[#1C3B2B] shadow-xs cursor-pointer"
                  >
                    Contact us
                  </button>
                ) : isCurrent ? (
                  <div className="space-y-2">
                    <div className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-emerald-300/80 bg-emerald-50 text-sm font-semibold text-emerald-800 shadow-xs select-none">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span>Current plan</span>
                    </div>
                    <Link
                      href="/dashboard/settings/subscription"
                      className="block text-center text-xs font-semibold text-slate-500 hover:text-slate-900 transition"
                    >
                      Manage in settings →
                    </Link>
                  </div>
                ) : isUserOrganizer ? (
                  <button
                    type="button"
                    onClick={() => setSwitchingPlan(plan)}
                    className={`flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold transition-all shadow-xs cursor-pointer ${
                      plan.is_popular || plan.key === 'silver' || isUpgrade
                        ? 'bg-[#1C3B2B] text-white hover:bg-[#152e22]'
                        : 'border border-stone-300 bg-white text-slate-800 hover:bg-stone-50'
                    }`}
                  >
                    {isUpgrade ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
                  </button>
                ) : (
                  <Link
                    href={
                      isAuthenticated
                        ? `/become-organizer?plan=${encodeURIComponent(plan.key || plan.id)}`
                        : `/register?redirect=become-organizer&plan=${encodeURIComponent(plan.key || plan.id)}`
                    }
                    className={`flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold transition-all shadow-xs cursor-pointer ${
                      plan.is_popular || plan.key === 'silver'
                        ? 'bg-[#1C3B2B] text-white hover:bg-[#152e22]'
                        : 'border border-stone-300 bg-white text-slate-800 hover:bg-stone-50'
                    }`}
                  >
                    Get started with {plan.name}
                  </Link>
                )
              }
            />
          );
        })}
      </section>

      <Dialog
        open={Boolean(switchingPlan)}
        onOpenChange={(open) => {
          if (!open && !switchingLoading) setSwitchingPlan(null);
        }}
        title={`Switch to ${switchingPlan?.name ?? ''} Plan`}
        description={`Confirm updating your organization tier to ${switchingPlan?.name ?? ''}.`}
      >
        {switchingPlan && (
          <div className="space-y-4 pt-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2.5 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Current plan:</span>
                <span className="font-semibold text-slate-900 capitalize">{currentPlanKey || 'Active'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">New plan:</span>
                <span className="font-semibold text-emerald-700">{switchingPlan.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Platform fee:</span>
                <span className="font-semibold text-slate-900">
                  {switchingPlan.key === 'free' || Number(switchingPlan.platform_fee_percent ?? switchingPlan.fees?.platform_fee_percent ?? switchingPlan.commission_percent ?? 0) === 0
                    ? '0.00% (Free community events)'
                    : `${Number(switchingPlan.platform_fee_percent ?? switchingPlan.fees?.platform_fee_percent ?? switchingPlan.commission_percent ?? 0).toFixed(2)}% + ${Number(switchingPlan.gst_percent ?? switchingPlan.fees?.gst_percent ?? 18)}% GST on platform fee`}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              Updating your organization plan takes effect immediately. All your events, activities, and ticketing features will automatically reflect the new tier rates.
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 pt-2 border-t border-slate-100">
              <Button
                variant="outline"
                disabled={switchingLoading}
                onClick={() => setSwitchingPlan(null)}
              >
                Cancel
              </Button>
              <Button
                disabled={switchingLoading}
                className="bg-[#1C3B2B] hover:bg-[#152e22] text-white"
                onClick={async () => {
                  if (!switchingPlan) return;
                  setSwitchingLoading(true);
                  try {
                    const currentOrder = PLAN_ORDER.findIndex((t) => (currentPlanKey || '').includes(t));
                    const targetOrder = planRank(switchingPlan);
                    const isDowngrade = currentOrder >= 0 && targetOrder < currentOrder;

                    if (isDowngrade) {
                      await subscriptionApi.downgrade({ plan_key: switchingPlan.key, plan_id: switchingPlan.id });
                      toast.success(`Successfully switched to ${switchingPlan.name} plan!`);
                    } else {
                      await subscriptionApi.upgrade({ plan_key: switchingPlan.key, plan_id: switchingPlan.id });
                      toast.success(`Successfully upgraded to ${switchingPlan.name} plan!`);
                    }

                    setCurrentPlanKey((switchingPlan.key || switchingPlan.name).toLowerCase());
                    setSwitchingPlan(null);
                  } catch (err: any) {
                    const respData = err?.response?.data;
                    if (respData?.code === 'DOWNGRADE_CONFLICTS') {
                      toast.error(respData.message || 'Cannot switch plan due to current event limits.');
                      setSwitchingPlan(null);
                      router.push(`/dashboard/settings/subscription?plan=${encodeURIComponent(switchingPlan.key || '')}`);
                    } else {
                      toast.error(getApiErrorMessage(err, 'Failed to update plan. Please try again.'));
                    }
                  } finally {
                    setSwitchingLoading(false);
                  }
                }}
              >
                {switchingLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  `Confirm Switch to ${switchingPlan.name}`
                )}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </MainLayout>
  );
}
